import React, { useState } from "react";
import HRHeader from "../components/HRHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Users, Building2, Briefcase, ScrollText, CalendarClock, Car, Laptop, Leaf, ShieldCheck, Globe, ShieldAlert, History, BadgeCheck, Timer } from "lucide-react";
import { CompanySwitcher } from "./admin/CompanySwitcher";
import { EmployeesTab } from "./admin/EmployeesTab";
import { EsgReadinessBanner } from "../components/EsgReadinessBanner";
import { DepartmentsTab } from "./admin/DepartmentsTab";
import { CcnlTab } from "./admin/CcnlTab";
import { AccrualsTab } from "./admin/AccrualsTab";
import { CarsTab } from "./admin/CarsTab";
import { DevicesTab } from "./admin/DevicesTab";
import { EsgTab } from "./admin/EsgTab";
import { AccessLogTab } from "./admin/AccessLogTab";
import { AccessMapTab } from "./admin/AccessMapTab";
import { IntrusionsTab } from "./admin/IntrusionsTab";
import { BlockHistoryTab } from "./admin/BlockHistoryTab";
import { WhitelistTab } from "./admin/WhitelistTab";
import { OvertimeExemptTab } from "./admin/OvertimeExemptTab";
import { ShiftHoursTab } from "./admin/ShiftHoursTab";

// Le schede raggruppate in sezioni tematiche così la barra non va mai fuori campo.
const SECTIONS = [
  { key: "people", label: "Anagrafiche", icon: Users, tabs: ["employees", "externals", "departments"] },
  { key: "contract", label: "Contratti e ratei", icon: ScrollText, tabs: ["ccnl", "accruals", "overtime_exempt", "shifthours"] },
  { key: "assets", label: "Beni e ESG", icon: Car, tabs: ["cars", "devices", "esg"] },
  { key: "security", label: "Sicurezza e accessi", icon: ShieldCheck, tabs: ["access", "accessmap", "security", "whitelist", "blockhistory"] },
];

const AdminPage = () => {
  const [companyFilter, setCompanyFilter] = useState("all");
  const [section, setSection] = useState("people");
  const [tab, setTab] = useState("employees");

  const TAB_DEFS = {
    employees: { label: "Dipendenti", icon: Users, testid: "tab-employees", render: () => <EmployeesTab companyFilter={companyFilter} /> },
    externals: { label: "Collaboratori esterni", icon: Briefcase, testid: "tab-externals", render: () => <EmployeesTab externalOnly companyFilter={companyFilter} /> },
    departments: { label: "Reparti", icon: Building2, testid: "tab-departments", render: () => <DepartmentsTab companyFilter={companyFilter} /> },
    ccnl: { label: "CCNL", icon: ScrollText, testid: "tab-ccnl", render: () => <CcnlTab /> },
    accruals: { label: "Maturazioni", icon: CalendarClock, testid: "tab-accruals", render: () => <AccrualsTab /> },
    overtime_exempt: { label: "Straordinari", icon: Timer, testid: "tab-overtime-exempt", render: () => <OvertimeExemptTab /> },
    shifthours: { label: "Turni", icon: CalendarClock, testid: "tab-shifthours", render: () => <ShiftHoursTab /> },
    cars: { label: "Auto aziendali", icon: Car, testid: "tab-cars", render: () => <CarsTab /> },
    devices: { label: "Dotazioni", icon: Laptop, testid: "tab-devices", render: () => <DevicesTab /> },
    esg: { label: "ESG", icon: Leaf, testid: "tab-esg", render: () => <EsgTab /> },
    access: { label: "Accessi", icon: ShieldCheck, testid: "tab-access", render: () => <AccessLogTab /> },
    accessmap: { label: "Mappa accessi", icon: Globe, testid: "tab-accessmap", render: () => <AccessMapTab /> },
    security: { label: "Sicurezza", icon: ShieldAlert, testid: "tab-security", render: () => <IntrusionsTab /> },
    whitelist: { label: "IP fidati", icon: BadgeCheck, testid: "tab-whitelist", render: () => <WhitelistTab /> },
    blockhistory: { label: "Storico blocchi", icon: History, testid: "tab-blockhistory", render: () => <BlockHistoryTab /> },
  };

  const currentSection = SECTIONS.find((s) => s.key === section) || SECTIONS[0];

  const selectSection = (s) => {
    setSection(s.key);
    setTab(s.tabs[0]);
  };

  return (
    <div className="min-h-screen bg-[#f0f2f5]">
      <HRHeader />
      <main className="max-w-[1200px] mx-auto px-4 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="text-[26px] font-black text-[#16345c] mb-1">Gestione HR</h1>
            <p className="text-[14px] text-gray-500">Gestisci dipendenti autorizzati e reparti aziendali</p>
          </div>
          <CompanySwitcher value={companyFilter} onChange={setCompanyFilter} />
        </div>

        <EsgReadinessBanner />

        {/* Selettore di sezione: raggruppa le schede per tema */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="admin-sections">
          {SECTIONS.map((s) => {
            const active = s.key === section;
            return (
              <button
                key={s.key}
                onClick={() => selectSection(s)}
                data-testid={`admin-section-${s.key}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-bold border transition-colors ${
                  active ? "bg-[#16345c] text-white border-[#16345c]" : "bg-white text-[#16345c] border-gray-200 hover:border-[#16345c]"
                }`}
              >
                <s.icon size={15} /> {s.label}
                <span className={`text-[10px] font-black leading-none px-1.5 py-0.5 rounded-full ${active ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"}`}>
                  {s.tabs.length}
                </span>
              </button>
            );
          })}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6 h-auto w-full md:w-auto max-w-full justify-start flex-nowrap overflow-x-auto no-scrollbar">
            {currentSection.tabs.map((tk) => {
              const t = TAB_DEFS[tk];
              return (
                <TabsTrigger key={tk} value={tk} className="flex items-center gap-2" data-testid={t.testid}>
                  <t.icon size={15} /> {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
          {Object.entries(TAB_DEFS).map(([tk, t]) => (
            <TabsContent key={tk} value={tk}>{t.render()}</TabsContent>
          ))}
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPage;
